import type { PhoneData, PhoneElements, PhoneInput } from './phone/types'
import { CountryService } from './phone/country-service'
import { PhoneFormatter } from './phone/phone-formatter'
import { PhoneUIManager } from './phone/ui-manager'

/**
 * Главный контроллер компонента ввода телефона
 * Координирует работу UI, форматирования и сервисов стран
 */
export class PhoneInputController {
  private elements: PhoneElements
  private countryService: CountryService
  private formatter: PhoneFormatter
  private uiManager: PhoneUIManager

  private phone: string = ''
  private code: string = ''
  private defaultPattern: string = ''
  // Номер набран в международном виде — с кодом страны: человек нажал «+»,
  // вставил номер с «+»/«00» или стёр код, чтобы набрать другой. Такой номер
  // не дополняем кодом страны страницы: клиенты бывают из любых стран, и
  // +380… не должен молча стать +7 (380)… (DEV-1602, ревью Kimi). Признак
  // берём из действий человека, а не из «+» в поле — его поле рисует само.
  private explicitCode: boolean = false
  // Вставка/автозаполнение: признак международного номера и курсор в конец
  // передаются из handlePaste в следующий handlePhoneInput.
  private pendingPaste: { explicitCode: boolean } | null = null
  private changeCallbacks: Array<(_data: PhoneData) => void> = []

  constructor(selector: string | PhoneInput) {
    const input =
      typeof selector === 'string'
        ? (document.querySelector(selector) as PhoneInput)
        : selector

    if (!input) {
      throw new Error('Phone input element not found')
    }

    const fieldWrapper = input.closest('[data-field]') as HTMLElement | null

    // Инициализация ссылок на DOM элементы
    this.elements = {
      wrapper: fieldWrapper || input,
      phoneInput: input
    }

    input.phoneInputController = this

    // Инициализация состояния из data-атрибутов
    this.phone = input.dataset.phoneValue || input.value || ''
    this.code = input.dataset.phoneCode || ''

    // Инициализация сервисов
    this.countryService = new CountryService()
    this.formatter = new PhoneFormatter()
    this.uiManager = new PhoneUIManager(this.elements)
  }

  /**
   * Инициализировать компонент
   */
  public async init(): Promise<void> {
    this.setupEventListeners()
    await this.loadCountries()
    this.updatePlaceholder()
    this.formatAndDisplayPhone()
  }

  /**
   * Установить обработчики событий через UI-менеджер
   */
  private setupEventListeners(): void {
    this.uiManager.setupEventListeners({
      onPhoneInput: (value, cursorPosition, inputType) =>
        this.handlePhoneInput(value, cursorPosition, inputType),
      onPaste: (value) => this.handlePaste(value),
      onPlus: () => {
        this.explicitCode = true
      }
    })
  }

  /**
   * Загрузить маски стран из meta
   */
  private async loadCountries(): Promise<void> {
    await this.countryService.fetchCountries()

    this.defaultPattern = this.countryService.getCountries()[0]?.pattern || ''
    this.formatter.setPattern(this.defaultPattern)

    this.explicitCode = this.looksInternational(this.phone)
    const initialDigits = this.resolveDigits(this.phone.replace(/\D/g, ''))
    if (initialDigits) {
      this.applyCountryByDigits(initialDigits)
    }
  }

  /**
   * Обработать ввод телефона
   * @param {string} value - Введённое значение
   * @param {number} cursorPosition - Текущая позиция курсора
   * @param {string} [inputType] - Тип ввода (например, 'insertText', 'deleteContentBackward')
   * @return {object} Объект с новым значением и позицией курсора
   */
  private handlePhoneInput(
    value: string,
    cursorPosition: number,
    inputType?: string
  ): { newValue: string; newCursorPosition: number } {
    const oldValue = this.phone
    const oldDigits = oldValue.replace(/\D/g, '')
    let digits = value.replace(/\D/g, '')
    const paste = this.pendingPaste
    this.pendingPaste = null
    const digitCursorPosition = this.formatter.countDigitsBeforeCursor(
      value,
      cursorPosition
    )

    if (paste) {
      this.explicitCode = paste.explicitCode
    } else if (digits && !value.trimStart().startsWith('+')) {
      // «+» поле рисует само, пропасть он может, только если значение заменили
      // целиком (выделили всё и набрали) или стёрли сам «+» — это новый ввод.
      this.explicitCode = false
    } else if (
      inputType?.startsWith('delete') &&
      digits.length === oldDigits.length - 1 &&
      digitCursorPosition <
        (this.countryService.findCountryByDigits(oldDigits)?.code.length ?? 0)
    ) {
      // Стёрли цифру кода страны — человек правит код сам, не возвращаем его.
      this.explicitCode = true
    }

    if (!digits && !paste) {
      // Поле очищено: следующий номер снова может быть местным. Исключение —
      // «+», набранный в пустое поле: на Android-клавиатурах он приходит не
      // keydown-ом (key=Unidentified), а сразу в значении поля.
      this.explicitCode = !oldValue && value.trimStart().startsWith('+')
    }

    if (digits === oldDigits) {
      if (inputType === 'deleteContentBackward' && oldDigits.length > 0) {
        const deleteAt = Math.max(0, digitCursorPosition - 1)
        digits = this.removeDigitAt(oldDigits, deleteAt)
      } else if (inputType === 'deleteContentForward' && oldDigits.length > 0) {
        const deleteAt = Math.min(digitCursorPosition, oldDigits.length - 1)
        digits = this.removeDigitAt(oldDigits, deleteAt)
      } else {
        const unmodifiedValue = this.normalizeDisplayValue(oldValue, value)
        return {
          newValue: unmodifiedValue,
          newCursorPosition: this.formatter.getCursorPositionForDigitIndex(
            unmodifiedValue,
            digitCursorPosition
          )
        }
      }
    }

    const resolvedDigits = this.resolveDigits(digits)
    this.applyCountryByDigits(resolvedDigits)
    const digitsForFormatting = this.getDigitsForFormatting(resolvedDigits)
    const formatted = this.formatter.format(digitsForFormatting)
    this.phone = this.normalizeDisplayValue(formatted, value)
    this.updatePlaceholder()

    // Подставленный код страны сдвигает курсор на свою длину (набрали «9» —
    // в поле «+7 (9»), иначе курсор остался бы перед последней цифрой.
    const updatedDigits = this.phone.replace(/\D/g, '')
    const boundedDigitCursor = Math.min(
      digitCursorPosition + Math.max(0, resolvedDigits.length - digits.length),
      updatedDigits.length
    )

    // После вставки курсор — в конец номера: браузер передаёт позицию в поле
    // до вставки (обычно 0), и следующая цифра встала бы в начало.
    const newCursorPosition = paste
      ? this.phone.length
      : this.formatter.getCursorPositionForDigitIndex(
          this.phone,
          boundedDigitCursor
        )

    this.emitData()

    return {
      newValue: this.phone,
      newCursorPosition
    }
  }

  /**
   * Привести набранные цифры к номеру с кодом страны (DEV-1602). Без этого
   * номер, начатый с «8», уходил в несуществующий код «+8…», маска
   * сбрасывалась, и поле принимало до 20 цифр.
   * - «89…» и «80[1-7]…» — таких кодов стран в мире нет, это российская
   *   «8» (→ «7») и белорусская «80» (→ «375»); меняем всегда, даже после «+»;
   * - международный номер (explicitCode) дальше не трогаем: чужая страна
   *   (+380, +998) остаётся как набрана, без маски;
   * - местный номер: «8…» → «7…»; цифры, которые ещё могут стать кодом страны
   *   с маской («3» → «375»), ждут следующих; остальное — номер без кода, к нему
   *   подставляется код страны страницы (так же номер без кода понимает сервер,
   *   Phonelib.default_country).
   * @param {string} digits - Введённые цифры
   * @return {string} Цифры с кодом страны
   */
  private resolveDigits(digits: string): string {
    const countries = this.countryService.getCountries()

    if (
      !digits ||
      countries.length === 0 ||
      this.countryService.findCountryByDigits(digits)
    ) {
      return digits
    }

    const hasCode = (code: string): boolean =>
      countries.some((country) => country.code === code)

    if (digits.startsWith('89') && hasCode('7')) {
      return `7${digits.slice(1)}`
    }

    if (/^80[1-7]/.test(digits) && hasCode('375')) {
      return `375${digits.slice(2)}`
    }

    // «8» и «80» — ещё не ясно, российский это номер или белорусский.
    if (digits === '8' || digits === '80') {
      return digits
    }

    if (this.explicitCode) {
      return digits
    }

    if (digits.startsWith('8') && hasCode('7')) {
      return `7${digits.slice(1)}`
    }

    if (countries.some((country) => country.code.startsWith(digits))) {
      return digits
    }

    return `${countries[0].code}${digits}`
  }

  /**
   * Удалить цифру по индексу
   * @param {string} digits - Строка цифр
   * @param {number} index - Индекс удаляемой цифры
   * @return {string} Строка без удалённой цифры
   */
  private removeDigitAt(digits: string, index: number): string {
    if (index < 0 || index >= digits.length) {
      return digits
    }

    return `${digits.slice(0, index)}${digits.slice(index + 1)}`
  }

  /**
   * Получить цифры для заполнения плейсхолдеров текущей маски
   * Если код страны уже присутствует в начале номера, исключаем его
   * @param {string} digits - Полный набор введённых цифр
   * @return {string} Цифры для подстановки в маску
   */
  private getDigitsForFormatting(digits: string): string {
    const pattern = this.formatter.getPattern()

    if (!pattern || !this.code) {
      return digits
    }

    if (digits.startsWith(this.code)) {
      return digits.slice(this.code.length)
    }

    return digits
  }

  /**
   * Обработать вставку или автозаполнение телефона
   * Возвращает только цифры, не больше 15 (E.164)
   * @param {string} value - Вставленное значение
   * @return {string} Очищенное значение
   */
  private handlePaste(value: string): string {
    const trimmed = value.trim()

    // change срабатывает и на blur после обычного набора — со значением, которое
    // поле нарисовало само: признак международного номера оно не меняет.
    if (trimmed === this.phone) {
      this.pendingPaste = { explicitCode: this.explicitCode }
      return trimmed.replace(/\D/g, '').slice(0, 15)
    }

    const digits = trimmed
      .replace(/^(\+|00)/, '')
      .replace(/\D/g, '')
      .slice(0, 15)

    this.pendingPaste = { explicitCode: this.looksInternational(trimmed) }
    return digits
  }

  /**
   * Похож ли готовый номер (вставка, автозаполнение, сохранённое значение) на
   * международный: с «+»/«00» или длиннее местного номера страны страницы —
   * тогда это номер с кодом страны, записанный без плюса («380931234567»;
   * в профиле телефон хранится цифрами без «+», см. User.normalize_phone).
   * @param {string} raw - Номер как есть
   * @return {boolean} true, если номер с кодом страны
   */
  private looksInternational(raw: string): boolean {
    const trimmed = raw.trim()
    if (/^(\+|00)/.test(trimmed)) {
      return true
    }

    const pageNumberLength = (
      this.countryService.getCountries()[0]?.pattern.match(/[0#]/g) || []
    ).length
    const digits = trimmed.replace(/\D/g, '')
    // «8» и ровно на цифру длиннее местного — российская запись с восьмёркой
    // («84951234567»), её разберёт resolveDigits как местный номер.
    if (digits.startsWith('8') && digits.length === pageNumberLength + 1) {
      return false
    }

    return pageNumberLength > 0 && digits.length > pageNumberLength
  }

  /**
   * Нормализовать отображаемое значение номера
   * @param {string} value - Значение для нормализации
   * @param {string} [rawInputValue] - Текущее «сырое» значение инпута
   * @return {string} Значение для отображения
   */
  private normalizeDisplayValue(value: string, rawInputValue?: string): string {
    const normalized = value.replace(/^\+/, '')
    const digits = normalized.replace(/\D/g, '')
    const rawDigits = rawInputValue?.replace(/\D/g, '') || ''

    if (digits.length > 0) {
      return `+${normalized}`
    }

    if (rawDigits.length > 0) {
      return `+${rawDigits}`
    }

    if (rawInputValue?.startsWith('+')) {
      return '+'
    }

    return ''
  }

  /**
   * Определить страну по первым цифрам и установить текущий паттерн
   * @param {string} digits - Введённые цифры
   */
  private applyCountryByDigits(digits: string): void {
    if (!digits) {
      this.code = ''
      this.formatter.setPattern(this.defaultPattern)
      return
    }

    const country = this.countryService.findCountryByDigits(digits)

    if (country) {
      this.code = country.code
      this.formatter.setPattern(country.pattern)
      return
    }

    this.code = ''
    this.formatter.setPattern('')
  }

  /**
   * Обновить placeholder на основе форматтера
   */
  private updatePlaceholder(): void {
    // Маска сбрасывается, когда введённый код страны неизвестен (например,
    // номер начали с 8). Подсказку в этом случае показываем по маске по
    // умолчанию, а не техническую строку — свободный ввод при этом сохраняется.
    const placeholder =
      this.formatter.getPlaceholder() ||
      PhoneFormatter.placeholderFromPattern(this.defaultPattern)
    this.uiManager.updatePlaceholder(placeholder)
  }

  /**
   * Отформатировать текущее значение телефона и отобразить
   */
  private formatAndDisplayPhone(): void {
    const digits = this.resolveDigits(this.phone.replace(/\D/g, ''))

    if (digits) {
      this.applyCountryByDigits(digits)
    } else {
      this.applyCountryByDigits('')
    }

    const digitsForFormatting = this.getDigitsForFormatting(digits)
    const formatted = this.formatter.format(digitsForFormatting)
    this.phone = this.normalizeDisplayValue(formatted, digits)
    this.uiManager.setPhoneValue(this.phone)
    this.updatePlaceholder()
  }

  /**
   * Передать данные телефона в колбэки
   */
  private emitData(): void {
    const data: PhoneData = {
      phone: this.phone,
      code: this.code
    }

    this.changeCallbacks.forEach((callback) => callback(data))
  }

  /**
   * Зарегистрировать колбэк изменения
   * @param {function} callback - Функция колбэка для вызова при изменении
   */
  public onChange(callback: (_data: PhoneData) => void): void {
    this.changeCallbacks.push(callback)
  }

  /**
   * Получить текущие данные телефона
   * @return {PhoneData} Текущий телефон и код
   */
  public getValue(): PhoneData {
    return {
      phone: this.phone,
      code: this.code
    }
  }

  public getFormattedValue(): string {
    const digits = this.phone.replace(/\D/g, '')

    if (!digits) {
      return ''
    }

    return this.code ? `+${digits}` : digits
  }

  /**
   * Установить значение телефона программно
   * @param {string} phone - Номер телефона для установки
   * @param {string} [code] - Опциональный код страны
   */
  public setValue(phone: string, code?: string): void {
    const digits = phone.replace(/\D/g, '')
    // Код передан отдельно — номер уже с кодом страны, подставлять свой нельзя.
    this.explicitCode = Boolean(code) || this.looksInternational(phone)
    this.phone = code ? `${code}${digits}` : digits

    this.formatAndDisplayPhone()
    this.emitData()
  }

  /**
   * Установить состояние ошибки
   * @param {boolean} hasError - Показывать ли состояние ошибки
   */
  public setError(hasError: boolean): void {
    this.uiManager.setError(hasError)
  }

  /**
   * Проверить валидность введённого номера телефона
   * @return {boolean} true если номер заполнен полностью согласно маске
   */
  public isValid(): boolean {
    const digitCount = this.phone.replace(/\D/g, '').length
    const pattern = this.formatter.getPattern()

    if (!pattern) {
      // Номер без маски: явно набранный код другой страны или страница без
      // масок — проверяем длину по E.164, точную проверку делает сервер
      // (Order#validate_bookable_phone!). Если маски есть, а код не набран
      // явно, номер не дописан («+3» на пути к +375) — не принимаем.
      if (this.countryService.getCountries().length > 0 && !this.explicitCode) {
        return false
      }

      return digitCount >= 8 && digitCount <= 15
    }

    const expectedDigits = (pattern.match(/[\d#]/g) || []).length
    return digitCount === expectedDigits
  }

  /**
   * Уничтожить компонент
   */
  public destroy(): void {
    this.changeCallbacks = []
    // Примечание: Очистку обработчиков событий можно улучшить
  }

  public getPhone(): string {
    return this.phone
  }
}

// Экспорт для глобального использования
if (typeof window !== 'undefined') {
  interface WindowWithPhone extends Window {
    PhoneInputController: typeof PhoneInputController
  }
  ;(window as unknown as WindowWithPhone).PhoneInputController =
    PhoneInputController
}
